import { Button, CircularProgress, FormControl, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { IVFormErros, VForm, VTextField, useVForm } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { SignatureServices } from "../../../../shared/services/api/signature/SignatureServices";
import { AutoCompleteClientLazy } from "../../components/AutoCompleteClientLazy";
import type { IEnvelopeData } from "../../types/types";

interface Props {
	active: boolean;
	isNew?: boolean;
	envelope?: IEnvelopeData;
	onEnvelopeSaved: (saved: IEnvelopeData) => void;
	signersCount?: number;
	documentsCount?: number;
}

const schema = yup.object({
	provider: yup.string().trim().required("Selecione o provedor."),
	name: yup.string().trim().min(3, "Informe ao menos 3 caracteres.").required("Informe o nome do envelope."),
	clientId: yup.string().trim().nullable().optional(), // torne obrigatório se quiser: .required("Selecione a imobiliária.")
	observations: yup.string().trim().max(2000, "Máximo de 2000 caracteres."),
	status: yup.string().oneOf(["draft", "close", "canceled", "running"], "Status inválido.").required(),
});

export default function EnvelopeTabDados({ active, isNew = false, envelope, onEnvelopeSaved }: Props) {
	const toast = useToast();
	const { formRef, save } = useVForm();
	const [loading, setLoading] = useState(false);

	const isLocked = Boolean(envelope?.id); // bloqueia edição após salvar/ter id

	// controla para não re-inicializar o form e apagar valores do usuário
	const didInitRef = useRef(false);
	const lastInitEnvelopeIdRef = useRef<string | undefined>(undefined);

	// Inicialização idempotente: só na primeira ativação ou quando muda o envelope.id
	useEffect(() => {
		if (!active) return;

		const incomingId = envelope?.id;
		const firstTime = !didInitRef.current;
		const idChanged = lastInitEnvelopeIdRef.current !== incomingId;

		if (!firstTime && !idChanged) {
			// já inicializado e id não mudou: não sobrescrever o form
			return;
		}

		didInitRef.current = true;
		lastInitEnvelopeIdRef.current = incomingId;

		const initial = {
			provider: envelope?.provider ?? "clicksign",
			name: envelope?.name ?? "",
			clientId: envelope?.clientId ?? "",
			observations: envelope?.observations ?? "",
			status: envelope?.status ?? "draft",
		};

		requestAnimationFrame(() => {
			formRef.current?.setData?.(initial);
			formRef.current?.setErrors?.({});
		});
	}, [active, envelope?.id]); // <- re-inicializa somente se mudar o id do envelope

	const setFormErrors = (yupError: yup.ValidationError) => {
		const validationErrors: IVFormErros = {};
		yupError.inner.forEach((err) => {
			if (err.path) validationErrors[err.path] = err.message;
		});
		formRef.current?.setErrors(validationErrors);
		toast.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title="Atenção" content="Verifique os campos destacados." />,
		});
	};

	const handleSubmit = async (values: any) => {
		try {
			if (isLocked) {
				toast.present({
					type: "info",
					position: "top-right",
					messageOrContent: <ToastContent title="Ação indisponível" content="Não é possível alterar um envelope já criado." />,
				});
				return;
			}

			// normaliza + valida
			const normalized = {
				provider: (values?.provider ?? "clicksign") as string,
				name: (values?.name ?? "").trim(),
				clientId: (values?.clientId ?? "").trim() || "",
				observations: (values?.observations ?? "").trim(),
				status: (values?.status ?? "draft") as string,
			};

			await schema.validate(normalized, { abortEarly: false });

			setLoading(true);
			const result = await SignatureServices.createEnvelope(normalized);
			if (result instanceof Error) throw new Error(result.message || "Erro ao criar envelope.");

			const saved = (result as any).data ?? result;
			if (!saved?.id) throw new Error("Criação concluída, mas sem ID retornado.");

			// Merge para não perder valores que a API não retornou
			const merged = {
				provider: saved?.provider ?? normalized.provider,
				name: saved?.name ?? normalized.name,
				clientId: saved?.clientId ?? normalized.clientId,
				observations: saved?.observations ?? normalized.observations,
				status: saved?.status ?? normalized.status,
			};

			// Atualiza o form com o salvo/merge e limpa erros — sem esperar o pai
			requestAnimationFrame(() => {
				formRef.current?.setData?.(merged);
				formRef.current?.setErrors?.({});
			});

			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Envelope criado." />,
			});

			// Notifica o pai (ele deverá colocar o envelope salvo e "travar" a aba)
			onEnvelopeSaved(saved as IEnvelopeData);
		} catch (e: any) {
			if (e instanceof yup.ValidationError) {
				setFormErrors(e);
			} else {
				toast.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={e?.message || "Erro ao salvar envelope."} />,
				});
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<VForm
			ref={formRef}
			onSubmit={handleSubmit}
			onPointerEnterCapture={undefined as any}
			onPointerLeaveCapture={undefined as any}
			placeholder={undefined as any}
		>
			{loading ? (
				<Stack alignItems="center" py={4}>
					<CircularProgress size={24} />
					<Typography variant="body2" mt={1}>
						Salvando…
					</Typography>
				</Stack>
			) : (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<FormControl sx={{ minWidth: "300px" }} fullWidth>
							<AutoCompleteClientLazy
								isExternalLoading={loading}
								namecampo="clientId"
								labelSelect="Imobiliaria"
								disabled={loading || isLocked}
								nameDefault={envelope?.client?.name ?? ""} // rótulo inicial (edição)
								minChars={2}
							/>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={6}>
						<VTextField name="status" select label="Status" fullWidth disabled>
							<MenuItem value="draft">Rascunho</MenuItem>
							<MenuItem value="close">Pronto</MenuItem>
							<MenuItem value="canceled">Cancelado</MenuItem>
							<MenuItem value="running">Em processo</MenuItem>
						</VTextField>
					</Grid>

					<Grid item xs={12} sm={8}>
						<VTextField name="name" label="Nome do envelope" fullWidth required disabled={isLocked} />
					</Grid>

					<Grid item xs={12}>
						<VTextField name="observations" label="Observações" fullWidth multiline minRows={3} disabled={isLocked} />
					</Grid>

					{!isLocked && (
						<Grid item xs={12}>
							<Stack direction="row" justifyContent="flex-end" spacing={1}>
								<Button variant="contained" onClick={save} disabled={loading}>
									{isNew ? "Criar envelope" : "Salvar"}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			)}
		</VForm>
	);
}
